import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Edit, TrendingUp, TrendingDown, Plus, Minus, Download, FileText, History } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BulkActions } from './BulkActions';
import { SplitPane } from './SplitPane';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UserPoints {
  id: string;
  user_name: string;
  user_email: string;
  current_balance: number;
  total_earned: number;
  total_redeemed: number;
  last_activity: string;
}

interface PointTransaction {
  id: string;
  points_amount: number;
  description: string;
  created_at: string;
  transaction_type: string;
}

interface RewardPointsProps {
  userPoints: UserPoints[];
  searchTerm: string;
  selectedUserId: string | null;
  pointsAdjustment: string;
  pointsReason: string;
  onSearchChange: (value: string) => void;
  onUserSelect: (userId: string) => void;
  onPointsAdjustmentChange: (value: string) => void;
  onPointsReasonChange: (value: string) => void;
  onAdjustPoints: () => void;
  isSpanish: boolean;
}

type SortField = 'user_name' | 'user_email' | 'current_balance' | 'total_earned' | 'total_redeemed' | 'last_activity';
type SortDirection = 'asc' | 'desc';

const RewardPoints: React.FC<RewardPointsProps> = ({
  userPoints,
  searchTerm,
  selectedUserId,
  pointsAdjustment,
  pointsReason,
  onSearchChange,
  onUserSelect,
  onPointsAdjustmentChange,
  onPointsReasonChange,
  onAdjustPoints,
  isSpanish
}) => {
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('current_balance');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedUser, setSelectedUser] = useState<UserPoints | null>(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [bulkAdjustment, setBulkAdjustment] = useState('');
  const [bulkReason, setBulkReason] = useState('');

  useEffect(() => {
    if (selectedUser) {
      fetchTransactions(selectedUser.id);
    }
  }, [selectedUser]);

  const fetchTransactions = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('points_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = userPoints.filter(user =>
      user.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      return ((aVal as number) > (bVal as number) ? 1 : -1) * direction;
    });
  }, [userPoints, searchTerm, sortField, sortDirection]);

  const handleBulkAdjust = async () => {
    if (!bulkAdjustment || !bulkReason) {
      toast.error(isSpanish ? 'Complete todos los campos' : 'Fill in all fields');
      return;
    }

    const amount = parseInt(bulkAdjustment);
    if (isNaN(amount) || amount === 0) {
      toast.error(isSpanish ? 'Cantidad inválida' : 'Invalid amount');
      return;
    }

    try {
      for (const userId of selectedUsers) {
        await supabase.from('points_transactions').insert({
          user_id: userId,
          points_amount: amount,
          description: bulkReason,
          transaction_type: 'adjusted',
          source: 'admin_adjustment',
          source_id: userId
        });
      }

      toast.success(
        isSpanish 
          ? `Puntos ajustados para ${selectedUsers.size} usuarios`
          : `Points adjusted for ${selectedUsers.size} users`
      );
      
      setSelectedUsers(new Set());
      setBulkAdjustment('');
      setBulkReason('');
      setShowAdjustDialog(false);
    } catch (error) {
      console.error('Error adjusting points:', error);
      toast.error(isSpanish ? 'Error al ajustar puntos' : 'Error adjusting points');
    }
  };

  const handleBulkExport = () => {
    const selectedData = userPoints.filter(u => selectedUsers.has(u.id));
    const csv = [
      ['User', 'Email', 'Current Balance', 'Total Earned', 'Total Redeemed', 'Last Activity'],
      ...selectedData.map(u => [
        u.user_name,
        u.user_email,
        u.current_balance,
        u.total_earned,
        u.total_redeemed,
        u.last_activity
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reward-points-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRowClick = (user: UserPoints) => {
    setSelectedUser(user);
    onUserSelect(user.id);
    if (!showSplitView) {
      setShowSplitView(true);
    }
  };

  const bulkActions = [
    {
      id: 'adjust',
      label: isSpanish ? 'Ajustar Puntos' : 'Adjust Points',
      icon: Edit,
      action: () => setShowAdjustDialog(true)
    },
    {
      id: 'export',
      label: isSpanish ? 'Exportar' : 'Export',
      icon: Download,
      action: handleBulkExport
    }
  ];

  const tableColumns = [
    {
      key: 'user_name',
      label: isSpanish ? 'Usuario' : 'User',
      sortable: true,
      className: 'font-medium'
    },
    {
      key: 'user_email',
      label: isSpanish ? 'Email' : 'Email',
      sortable: true
    },
    {
      key: 'current_balance',
      label: isSpanish ? 'Balance Actual' : 'Current Balance',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center gap-1">
          <TrendingUp className="w-4 h-4 text-green-600" />
          <span className="font-semibold">{value}</span>
        </div>
      )
    },
    {
      key: 'total_earned',
      label: isSpanish ? 'Total Ganado' : 'Total Earned',
      sortable: true
    },
    {
      key: 'total_redeemed',
      label: isSpanish ? 'Total Canjeado' : 'Total Redeemed',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center gap-1">
          <TrendingDown className="w-4 h-4 text-red-600" />
          {value}
        </div>
      )
    },
    {
      key: 'last_activity',
      label: isSpanish ? 'Última Actividad' : 'Last Activity',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
      key: 'actions',
      label: isSpanish ? 'Acciones' : 'Actions',
      render: (_: any, user: UserPoints) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(user);
          }}
        >
          <Edit className="w-4 h-4 mr-1" />
          {isSpanish ? 'Ajustar' : 'Adjust'}
        </Button>
      )
    }
  ];

  const UserDetailView = ({ user }: { user: UserPoints }) => (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-4">
          {isSpanish ? 'Detalles de Puntos' : 'Points Details'}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{isSpanish ? 'Balance Actual' : 'Current Balance'}</p>
            <p className="text-3xl font-bold text-green-600">{user.current_balance}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{isSpanish ? 'Total Ganado' : 'Total Earned'}</p>
            <p className="text-3xl font-bold">{user.total_earned}</p>
          </CardContent>
        </Card>
      </div>

      <div>
        <Label htmlFor="adjustment">
          {isSpanish ? 'Ajuste de Puntos' : 'Points Adjustment'}
        </Label>
        <div className="flex gap-2 mt-2">
          <Input
            id="adjustment"
            type="number"
            placeholder={isSpanish ? 'Ej: +100 o -50' : 'e.g. +100 or -50'}
            value={pointsAdjustment}
            onChange={(e) => onPointsAdjustmentChange(e.target.value)}
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {isSpanish 
            ? `Balance actual: ${user.current_balance}`
            : `Current balance: ${user.current_balance}`
          }
        </p>
      </div>

      <div>
        <Label htmlFor="reason">
          {isSpanish ? 'Razón del Ajuste' : 'Reason for Adjustment'}
        </Label>
        <Textarea
          id="reason"
          placeholder={isSpanish ? 'Explica por qué se ajustan los puntos...' : 'Explain why points are being adjusted...'}
          value={pointsReason}
          onChange={(e) => onPointsReasonChange(e.target.value)}
          className="mt-2"
        />
      </div>

      <Button 
        onClick={onAdjustPoints}
        disabled={!pointsAdjustment || !pointsReason}
        className="w-full"
      >
        {isSpanish ? 'Confirmar Ajuste' : 'Confirm Adjustment'}
      </Button>

      <div className="pt-4 border-t">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <History className="w-4 h-4" />
          {isSpanish ? 'Historial de Transacciones' : 'Transaction History'}
        </h4>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {transactions.map((txn) => (
            <div key={txn.id} className="p-3 border rounded-lg">
              <div className="flex justify-between items-start mb-1">
                <Badge variant={txn.points_amount > 0 ? 'default' : 'secondary'}>
                  {txn.points_amount > 0 ? '+' : ''}{txn.points_amount}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(txn.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm">{txn.description}</p>
            </div>
          ))}
          {transactions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {isSpanish ? 'No hay transacciones' : 'No transactions'}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const mainContent = (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              {isSpanish ? 'Puntos de Recompensa' : 'Reward Points'}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSplitView(!showSplitView)}
            >
              <FileText className="w-4 h-4 mr-2" />
              {showSplitView 
                ? (isSpanish ? 'Vista Simple' : 'Simple View')
                : (isSpanish ? 'Vista Dividida' : 'Split View')
              }
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <Input
              placeholder={isSpanish ? 'Buscar por nombre o email...' : 'Search by name or email...'}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="max-w-md"
            />
          </div>

          {selectedUsers.size > 0 && (
            <BulkActions
              selectedCount={selectedUsers.size}
              actions={bulkActions}
              onClearSelection={() => setSelectedUsers(new Set())}
              isSpanish={isSpanish}
            />
          )}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isSpanish ? 'Usuario' : 'User'}</TableHead>
                  <TableHead>{isSpanish ? 'Email' : 'Email'}</TableHead>
                  <TableHead>{isSpanish ? 'Balance Actual' : 'Current Balance'}</TableHead>
                  <TableHead>{isSpanish ? 'Total Ganado' : 'Total Earned'}</TableHead>
                  <TableHead>{isSpanish ? 'Total Canjeado' : 'Total Redeemed'}</TableHead>
                  <TableHead>{isSpanish ? 'Última Actividad' : 'Last Activity'}</TableHead>
                  <TableHead>{isSpanish ? 'Acciones' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedUsers.map(user => (
                  <TableRow key={user.id} onClick={() => handleRowClick(user)} className="cursor-pointer">
                    <TableCell className="font-medium">{user.user_name}</TableCell>
                    <TableCell>{user.user_email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="font-semibold">{user.current_balance}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.total_earned}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        {user.total_redeemed}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(user.last_activity).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleRowClick(user); }}>
                        <Edit className="w-4 h-4 mr-1" />
                        {isSpanish ? 'Ajustar' : 'Adjust'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (showSplitView && selectedUser) {
    return (
      <SplitPane
        left={mainContent}
        right={<UserDetailView user={selectedUser} />}
        defaultSize={60}
      />
    );
  }

  return (
    <>
      {mainContent}
      
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isSpanish ? 'Ajustar Puntos en Lote' : 'Bulk Adjust Points'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {isSpanish 
                ? `Ajustar puntos para ${selectedUsers.size} usuarios seleccionados`
                : `Adjust points for ${selectedUsers.size} selected users`
              }
            </p>
            <div>
              <Label htmlFor="bulk-adjustment">
                {isSpanish ? 'Ajuste de Puntos' : 'Points Adjustment'}
              </Label>
              <Input
                id="bulk-adjustment"
                type="number"
                placeholder={isSpanish ? 'Ej: +100 o -50' : 'e.g. +100 or -50'}
                value={bulkAdjustment}
                onChange={(e) => setBulkAdjustment(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="bulk-reason">
                {isSpanish ? 'Razón' : 'Reason'}
              </Label>
              <Textarea
                id="bulk-reason"
                placeholder={isSpanish ? 'Explica por qué...' : 'Explain why...'}
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleBulkAdjust}
              disabled={!bulkAdjustment || !bulkReason}
              className="w-full"
            >
              {isSpanish ? 'Confirmar Ajuste' : 'Confirm Adjustment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RewardPoints;
